import { useId, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { EmptyState, ErrorState, LoadingState } from "@/components/PageUi";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { InfoHint } from "@/components/InfoHint";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useBooks, type BooksLinks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import { useMonthlySummary, usePeriod, type Period } from "./period";
import { PeriodRuler } from "./PeriodRuler";
import type {
  Book,
  BankAccount,
  JournalSourceEvent,
  JournalSourceType,
  LedgerAccount,
  PlatformTaxRate,
  Posting,
  ReportingBasis,
} from "./types";

/** The API sends decimals as number or string. */
export const num = (v: number | string | null | undefined) => (v == null || v === "" ? 0 : Number(v));

/** Books are GBP only for now (D3). */
export const money = (v: number | string | null | undefined) => formatCurrency(num(v), "GBP");

/** A blank cell rather than "£0.00", so a trial balance reads as debit or credit at a glance. */
export const moneyOrBlank = (v: number | string | null | undefined) => (num(v) === 0 ? "" : money(v));

/** Dr or Cr after the amount: a debit and credit pair in one column. */
export function drCr(debit: number | string | null | undefined, credit: number | string | null | undefined) {
  const n = num(debit) - num(credit);
  if (n === 0) return "";
  return `${money(Math.abs(n))} ${n > 0 ? "Dr" : "Cr"}`;
}

export const today = () => new Date().toISOString().slice(0, 10);

export function accountLabel(a: { code?: string | null; displayId?: string | null; name?: string | null }) {
  const code = a.code ?? a.displayId;
  return `${code ? `${code} · ` : ""}${a.name || "Unnamed"}`;
}

export function taxRateLabel(rate: PlatformTaxRate) {
  // QuickBooks' code is its internal numeric id: meaningless to a person.
  return `${rate.code && !/^\d+$/.test(rate.code) ? `${rate.code} · ` : ""}${rate.name ?? "Unnamed"}${
    rate.rate != null ? ` · ${rate.rate}%` : ""
  }`;
}

export const SOURCE_LABEL: Record<JournalSourceType, string> = {
  OpeningBalances: "Opening balances",
  Invoice: "Invoice",
  InvoicePayment: "Invoice payment",
  CreditNote: "Credit note",
  Bill: "Bill",
  SupplierCreditNote: "Supplier credit note",
  Expense: "Expense",
  BankLine: "Bank transaction",
  StripePayout: "Stripe payout",
  Manual: "Manual",
  BillPayment: "Bill payment",
};

export const EVENT_LABEL: Record<JournalSourceEvent, string> = {
  OpeningBalances: "Opening balances",
  InvoiceIssued: "Issued",
  InvoicePaid: "Paid",
  InvoiceVoided: "Voided",
  CreditNoteIssued: "Credit note issued",
  EarlyPaymentDiscount: "Early-payment discount",
  BillApproved: "Approved",
  BillLineRecoded: "Line recoded",
  BillPaid: "Paid",
  SupplierCreditIssued: "Supplier credit issued",
  SupplierCreditApplied: "Supplier credit applied",
  SupplierCreditReversed: "Supplier credit reversed",
  ExpenseCodingSettled: "Coding settled",
  BankLineSuspense: "Parked in Uncategorised",
  BankLineCategorised: "Categorised",
  BankLineMatched: "Matched",
  StripePayoutPaid: "Payout paid",
  Manual: "Manual",
  Reversal: "Reversal",
};

/** Bank transactions with that one opened: its sheet, and its row highlighted where it is shown. */
export function bankLineHref(lineId: string, status: "all" | "Matched" = "all") {
  return `/books/bank-lines?status=${status}&highlight=${lineId}`;
}

/** "Invoice · Issued"; one label when the event only repeats the source ("Opening balances"). */
export function sourceEventLabel(type: JournalSourceType | undefined, event: JournalSourceEvent | undefined) {
  const source = type ? SOURCE_LABEL[type] : "";
  const what = event ? EVENT_LABEL[event] : "";
  return !what || what === source ? source : `${source} · ${what}`;
}

/** The host page a source document opens on; null when it has no page of its own (a payment, a payout). */
export function sourceHref(links: BooksLinks, type: JournalSourceType | undefined, id: string | undefined): string | null {
  if (!type || !id) return null;
  switch (type) {
    case "Invoice":
      return links.invoice(id);
    case "Bill":
      return links.bill(id);
    case "Expense":
      return links.expense(id);
    case "CreditNote":
      return links.creditNote(id);
    case "SupplierCreditNote":
      return links.supplierCreditNote(id);
    case "BankLine":
      return bankLineHref(id, "all");
    default:
      return null;
  }
}

export function PostingStatusBadge({ posting, className }: { posting: Pick<Posting, "status" | "reason">; className?: string }) {
  const tone = (() => {
    switch (posting.status) {
      case "Posted":
        return { dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-400", label: "In the books" };
      case "Pending":
        return { dot: "bg-sky-500 animate-pulse", text: "text-sky-700 dark:text-sky-400", label: "Posting" };
      case "Waiting":
        return { dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-400", label: "Waiting" };
      case "Skipped":
        return { dot: "bg-muted-foreground/60", text: "text-muted-foreground", label: "Not posted" };
      case "Failed":
        return { dot: "bg-destructive", text: "text-destructive", label: "Failed" };
      default:
        return { dot: "bg-muted-foreground/40", text: "text-muted-foreground", label: "Not in the books" };
    }
  })();
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium whitespace-nowrap", tone.text, className)}>
        <span className={cn("size-1.5 rounded-full", tone.dot)} />
        {tone.label}
      </span>
      {posting.reason && posting.status !== "Posted" ? <InfoHint label="Why">{posting.reason}</InfoHint> : null}
    </span>
  );
}

/** Books minus platform. Any non-zero difference is the point of the column, so it is loud. */
export function DifferenceCell({ value, compared }: { value: number | string | null | undefined; compared: boolean }) {
  if (!compared) return null;
  const n = num(value);
  return (
    <span
      className={cn(
        "tabular-nums",
        n === 0 ? "text-muted-foreground" : "rounded bg-amber-500/15 px-1.5 py-0.5 font-semibold text-amber-800 dark:text-amber-300",
      )}
    >
      {n === 0 ? "-" : money(n)}
    </span>
  );
}

const NONE = "__none__";

export function AccountSelect({
  id,
  label,
  hint,
  value,
  accounts,
  disabled,
  optional,
  noneLabel = "None",
  onChange,
}: {
  id: string;
  label?: string;
  hint?: string;
  value: string | null | undefined;
  accounts: LedgerAccount[];
  disabled?: boolean;
  /** Offers no account as a choice, labelled `noneLabel`. */
  optional?: boolean;
  noneLabel?: string;
  onChange: (id: string | null) => void;
}) {
  return (
    <div className="space-y-1.5">
      {label ? (
        <Label htmlFor={id} className="flex items-center gap-2">
          {label}
          {!value && !optional ? (
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Not set</span>
          ) : null}
        </Label>
      ) : null}
      <Select
        value={value ?? (optional ? NONE : undefined)}
        onValueChange={(v) => onChange(v === NONE ? null : v)}
        disabled={disabled || accounts.length === 0}
      >
        <SelectTrigger id={id} className={cn("w-full", !value && !optional && !disabled && "border-amber-500/60")}>
          <SelectValue placeholder={accounts.length === 0 ? "No accounts synced" : "Choose an account"} />
        </SelectTrigger>
        <SelectContent>
          {optional ? <SelectItem value={NONE}>{noneLabel}</SelectItem> : null}
          {accounts.map((a) => (
            <SelectItem key={a.id} value={a.id!}>
              {accountLabel(a)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function TaxRateSelect({
  id,
  value,
  rates,
  onChange,
}: {
  id: string;
  value: string | null;
  rates: PlatformTaxRate[];
  onChange: (id: string | null) => void;
}) {
  return (
    <Select value={value ?? NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
      <SelectTrigger id={id} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>No VAT</SelectItem>
        {rates.map((r) => (
          <SelectItem key={r.id} value={r.id!}>
            {taxRateLabel(r)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The open book (Setup or Active), or null when the organisation has none. */
export function useCurrentBook() {
  const { books } = useBooks();
  return useLoad<Book | null>(() => books.GET("/v1/books/current", {}), [books], { notFound: null });
}

export function useChart() {
  const { platform } = useBooks();
  return useLoad<LedgerAccount[]>(() => platform.GET("/v1/accounting/ledger-accounts", {}), [platform]);
}

export function useTaxRates() {
  const { platform } = useBooks();
  return useLoad<PlatformTaxRate[]>(() => platform.GET("/v1/accounting/tax-rates", {}), [platform]);
}

/** Every connected bank account, with its institution's name for display. */
export function useBankAccounts() {
  const { platform } = useBooks();
  // Returned whole, unpaged in practice: an organisation has a handful.
  const loaded = useLoad(() => platform.GET("/v1/bank-connections", {}), [platform]);
  const accounts: Array<BankAccount & { institutionName: string }> = (loaded.data?.data ?? []).flatMap((c) =>
    c.accounts.map((a) => ({ ...a, institutionName: c.institutionName })),
  );
  return { ...loaded, accounts };
}

export function bankAccountLabel(a: { name: string; institutionName?: string; accountNumber?: string | null }) {
  return `${a.institutionName ? `${a.institutionName} · ` : ""}${a.name}${a.accountNumber ? ` ····${a.accountNumber.slice(-4)}` : ""}`;
}

/**
 * Every books screen but Home and Setup needs a book. Renders the header, then the loading, error and
 * "no books yet" states, so each screen only draws its own content.
 */
export function BooksScreen({
  title,
  subtitle,
  actions,
  size,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  size?: "default" | "narrow";
  /** `reload` re-reads the book after a write that changes it. */
  children: (book: Book, reload: () => Promise<void>) => ReactNode;
}) {
  const current = useCurrentBook();
  return (
    <PageContainer size={size}>
      <PageHeader eyebrow="Books" title={title} subtitle={subtitle} actions={current.data ? actions : undefined} />
      {current.loading && current.data === undefined ? (
        <LoadingState label="Loading your books" />
      ) : current.error ? (
        <ErrorState message={apiErrorMessage(current.error, "Couldn't load your books.")} onRetry={current.reload} />
      ) : !current.data ? (
        <EmptyState
          icon={BookOpen}
          title="No books yet"
          action={
            <Button asChild size="sm">
              <Link to="/books">Go to Books home</Link>
            </Button>
          }
        >
          Start a set of books from Books home to see this.
        </EmptyState>
      ) : (
        children(current.data, current.reload)
      )}
    </PageContainer>
  );
}

/**
 * A Books page with dated data (D49): the period slider under the header, and the period handed to the page. Report
 * pages (`basis`) also get the accrual/cash switch beside it.
 */
export function BooksPeriodScreen({
  title,
  subtitle,
  actions,
  basis,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  basis?: boolean;
  children: (book: Book, period: Period, reload: () => Promise<void>) => ReactNode;
}) {
  return (
    <BooksScreen title={title} subtitle={subtitle} actions={actions}>
      {(book, reload) => <PeriodScope book={book} basis={basis} render={(period) => children(book, period, reload)} />}
    </BooksScreen>
  );
}

// Mounted only on pages with a slider, so pages without one never get a period written into their URL.
function PeriodScope({ book, basis, render }: { book: Book; basis?: boolean; render: (period: Period) => ReactNode }) {
  const period = usePeriod(book);
  const summary = useMonthlySummary(period.basis);
  return (
    <>
      <PeriodRuler period={period} bars={summary.data?.months} trailing={basis ? <BasisToggle period={period} /> : undefined} />
      {render(period)}
    </>
  );
}

const BASIS_LABEL: Record<ReportingBasis, string> = { Accrual: "Accrual", Cash: "Cash" };

/** Accrual | Cash. Follows the period from page to page; the book's setting is only the default. */
export function BasisToggle({ period }: { period: Pick<Period, "basis" | "setBasis"> }) {
  const labelId = useId();
  return (
    <div className="flex items-center gap-1">
      <span id={labelId} className="sr-only">
        Reporting basis
      </span>
      <div role="radiogroup" aria-labelledby={labelId} className="inline-flex h-8 items-center rounded-md bg-muted p-0.5 text-muted-foreground">
        {(["Accrual", "Cash"] as const).map((b) => (
          <button
            key={b}
            type="button"
            role="radio"
            aria-checked={period.basis === b}
            onClick={() => period.basis !== b && period.setBasis(b)}
            className={cn(
              "rounded-sm px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              period.basis === b ? "bg-background text-foreground shadow-sm" : "hover:text-foreground",
            )}
          >
            {BASIS_LABEL[b]}
          </button>
        ))}
      </div>
      <InfoHint label="Accrual and cash basis" side="bottom">
        Accrual counts a sale when the invoice is issued and a cost when the bill is approved. Cash counts them when they are
        paid, in proportion to each payment, VAT included.
      </InfoHint>
    </div>
  );
}
